import TargetGroupRepository from '../repositories/TargetGroupRepository'
import CustomPropertyRepository from '../repositories/CustomPropertyRepository'
import FlagsRepository from '../repositories/RoxFlagRepository'
import ExperimentRepository from '../repositories/ExperimentsRepository'
import RoxxParser from '../parsers/RoxxParser'
import md5Module from 'md5'
import {decode} from 'js-base64'
import {DynamicPropertiesHandler} from './DynamicProperties'
import {ExceptionTrigger} from './UserspaceUnhandledErrorInvoker'

function _versionCompare(
  v1,
  v2,
  options: any = {zeroExtend: true, lexicographical: true}
) {
  const lexicographical = options && options.lexicographical
  const zeroExtend = options && options.zeroExtend
  let v1parts = v1.split('.')
  let v2parts = v2.split('.')

  function isValidPart(x) {
    return (lexicographical ? /[0-9A-Za-z_-]+$/ : /^\d+$/).test(x)
  }

  if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
    return NaN
  }

  if (zeroExtend) {
    while (v1parts.length < v2parts.length) v1parts.push('0')
    while (v2parts.length < v1parts.length) v2parts.push('0')
  }

  if (!lexicographical) {
    v1parts = v1parts.map(Number)
    v2parts = v2parts.map(Number)
  }

  for (let i = 0; i < v1parts.length; ++i) {
    if (v2parts.length == i) {
      return 1
    }

    if (v1parts[i] == v2parts[i]) {
      continue
    } else if (v1parts[i] > v2parts[i]) {
      return 1
    } else {
      return -1
    }
  }

  if (v1parts.length != v2parts.length) {
    return -1
  }

  return 0
}

const getBucket = (seed) => {
  let hash = md5Module(seed, {asBytes: true})
  hash =
    ((hash[0] & 0xff) |
      ((hash[1] & 0xff) << 8) |
      ((hash[2] & 0xff) << 16) |
      ((hash[3] & 0xff) << 24)) >>>
    0

  const bucket = hash / (Math.pow(2, 32) - 1)
  return bucket
}

export const isUndefined = (op) => op === undefined
export const now = () => Date.now()
export const and = (op1, op2) => op1 && op2
export const or = (op1, op2) => op1 || op2
export const ne = (op1, op2) =>
  (isUndefined(op1) ? false : op1) !== (isUndefined(op2) ? false : op2)
export const eq = (op1, op2) =>
  (isUndefined(op1) ? false : op1) === (isUndefined(op2) ? false : op2)
export const not = (op) => !op
export const ifThen = (conditionExpression, trueExpression, falseExpression) =>
  conditionExpression ? trueExpression : falseExpression

export const lt = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'number') {
    op1 = Number(op1)
    if (isNaN(op1)) {
      return false
    }
  }
  if (typeof op2 !== 'number') {
    op2 = Number(op2)
    if (isNaN(op2)) {
      return false
    }
  }
  return op1 < op2
}

export const lte = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'number') {
    op1 = Number(op1)
    if (isNaN(op1)) {
      return false
    }
  }
  if (typeof op2 !== 'number') {
    op2 = Number(op2)
    if (isNaN(op2)) {
      return false
    }
  }
  return op1 <= op2
}

export const tsToNum = (op1) => {
  if (isUndefined(op1)) return undefined

  if (op1 instanceof Date) {
    return op1.getTime() / 1000 // epoc is in seconds, getTime in miliseconds
  }
  return undefined
}

export const gt = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'number') {
    op1 = Number(op1)
    if (isNaN(op1)) {
      return false
    }
  }
  if (typeof op2 !== 'number') {
    op2 = Number(op2)
    if (isNaN(op2)) {
      return false
    }
  }
  return op1 > op2
}

export const numne = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'number') {
    op1 = Number(op1)
    if (isNaN(op1)) {
      return false
    }
  }
  if (typeof op2 !== 'number') {
    op2 = Number(op2)
    if (isNaN(op2)) {
      return false
    }
  }

  return op1 !== op2
}

export const numeq = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'number') {
    op1 = Number(op1)
    if (isNaN(op1)) {
      return false
    }
  }
  if (typeof op2 !== 'number') {
    op2 = Number(op2)
    if (isNaN(op2)) {
      return false
    }
  }

  return op1 === op2
}

export const gte = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'number') {
    op1 = Number(op1)
    if (isNaN(op1)) {
      return false
    }
  }
  if (typeof op2 !== 'number') {
    op2 = Number(op2)
    if (isNaN(op2)) {
      return false
    }
  }

  return op1 >= op2
}

export const match = (op1, op2, op3) => {
  const text = op1
  const regex = new RegExp(op2, op3)

  const match = regex.exec(text)
  if (match) {
    return true
  }

  return false
}

export const semverLt = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'string' || typeof op2 !== 'string') return false

  return _versionCompare(op1, op2, {zeroExtend: true}) < 0
}

export const semverLte = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'string' || typeof op2 !== 'string') return false

  return _versionCompare(op1, op2, {zeroExtend: true}) <= 0
}

export const semverGt = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'string' || typeof op2 !== 'string') return false

  return _versionCompare(op1, op2, {zeroExtend: true}) > 0
}

export const semverGte = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'string' || typeof op2 !== 'string') return false

  return _versionCompare(op1, op2, {zeroExtend: true}) >= 0
}

export const semverEq = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'string' || typeof op2 !== 'string') return false

  return _versionCompare(op1, op2) == 0
}

export const semverNe = (op1, op2) => {
  if (isUndefined(op1) || isUndefined(op2)) return false

  if (typeof op1 !== 'string' || typeof op2 !== 'string') return false

  return _versionCompare(op1, op2) != 0
}

export const mergeSeed = (seed1, seed2) => seed1 + '.' + seed2

export const isInPercentage = (percentage, seed) => {
  const bucket = getBucket(seed)
  const isInPercentage = bucket <= percentage

  return isInPercentage
}

export const isInPercentageRange = (percentageLow, percentageHigh, seed) => {
  const bucket = getBucket(seed)
  const isInPercentage = bucket >= percentageLow && bucket <= percentageHigh

  return isInPercentage
}

export const flagValue = (flagName, context = {}, internalContext = {}) => {
  const flag = FlagsRepository.flagWithName(flagName)
  if (flag) {
    const contextClone: any = {...internalContext}
    flag.getInternalValue(contextClone, context)
    if (
      !contextClone.isPeek &&
      !contextClone.result.isOverride &&
      !contextClone.result.isFreezed
    ) {
      flag._flagImpression(contextClone.result.value, contextClone.result.usedContext)
    }
    return contextClone.result.value
  }
  const exp = ExperimentRepository.experimentForFlagName(flagName)
  if (exp && exp.deploymentConfiguration) {
    const expressionValue = new RoxxParser().evaluateExpression(
      exp.deploymentConfiguration.condition,
      internalContext,
      context
    )

    return expressionValue || 'false'
  }

  return 'false'
}

export const isInTargetGroup = (targetGroup, context = {}, callContext = {}) => {
  const tg = TargetGroupRepository.targetGroupWithName(targetGroup)
  if (tg) {
    return new RoxxParser().evaluateExpression(tg.condition, callContext, context)
  } else {
    return false
  }
}

export const isTargetGroupPaired = () => false

export const property = (propName, context = {}) => {
  const prop = CustomPropertyRepository.get(propName)
  if (!prop) {
    const handler: any = DynamicPropertiesHandler && DynamicPropertiesHandler()
    let handlerResult
    if (handler) {
      if (!handler._isUserDefined) {
        handlerResult = handler(propName, context)
      } else {
        try {
          handlerResult = handler(propName, context)
        } catch (err) {
          err.isUserError = true
          err.trigger = ExceptionTrigger.DYNAMIC_PROPERTIES_RULE
          throw err
        }
      }
    }
    return handlerResult
  } else {
    return prop.getValue(context)
  }
}

export const inArray = (target, list) => (list ? list.includes(target) : false)

export const md5 = (str) => {
  if (typeof str !== 'string') {
    return undefined
  }
  return md5Module(str)
}

export const concat = (str1, str2) => {
  if (typeof str1 !== 'string' || typeof str2 !== 'string') {
    return undefined
  }
  return `${str1}${str2}`
}

export const b64d = (value) => {
  if (typeof value !== 'string') {
    return undefined
  }
  return decodeURIComponent(decode(value))
}

export const operatorsWithContext = [isInTargetGroup, flagValue, property]
